"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  OAuthProvider as FirebaseOAuthProvider,
  GoogleAuthProvider,
  getMultiFactorResolver,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { GoogleIcon } from "@/components/common/GoogleIcon"
import { Link } from "@/components/common/Link/Link"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { defaultFormValidation } from "@/lib/actions/formProps"
import { getFirebaseAuth } from "@/lib/auth/getFirebaseAuth"
import { loginAction } from "../actions/login"
import { trackLogIn, trackSso } from "../analytics/events"
import { isPreviewInstance, signInGoogleViaProxy } from "../utils/previewAuth"
import { resolveLoginResult } from "../utils/resolveLoginResult"
import { type LoginInput, loginSchema } from "../validators/login"
import { AppleIcon } from "./AppleIcon"
import { MfaVerifyForm } from "./MfaVerifyForm"

type FirebaseAuthError = { code?: string }

type MfaState = {
  resolver: ReturnType<typeof getMultiFactorResolver>
  verificationId: string
  phoneHint: string
}

type OAuthProvider = "google" | "apple"

type LoginActionResult = Awaited<ReturnType<typeof loginAction>>

type LoginErrorKey =
  | "invalidCredentials"
  | "tooManyAttempts"
  | "userDisabled"
  | "popupBlocked"
  | "popupCancelled"
  | "network"
  | "generic"

// Cases are disjoint between the two flows (email/password produces
// `invalid-credential`, OAuth produces `popup-*`), so a single map covers both.
const firebaseAuthErrorToMessageKey = (code?: string): LoginErrorKey => {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/invalid-email":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "invalidCredentials"
    case "auth/too-many-requests":
      return "tooManyAttempts"
    case "auth/user-disabled":
      return "userDisabled"
    case "auth/popup-blocked":
      return "popupBlocked"
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "popupCancelled"
    case "auth/network-request-failed":
      return "network"
    default:
      return "generic"
  }
}

export type LoginFormProps = {
  /**
   * Already validated by the shared `loginSearchParams.redirect` parser
   * (both the server loader and `LoginFormWithRedirect`'s client read go
   * through it) — this component trusts the value rather than re-validating.
   */
  redirectTo?: string
}

export const LoginForm = ({ redirectTo = "/" }: LoginFormProps) => {
  const t = useTranslations("auth.login")
  const tErrors = useTranslations("auth.login.errors")
  const router = useRouter()
  const recaptchaContainerRef = useRef<HTMLDivElement>(null)
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null)
  const [mfaState, setMfaState] = useState<MfaState | null>(null)
  const [oauthPending, setOauthPending] = useState<OAuthProvider | null>(null)
  const [isNavigating, setIsNavigating] = useState(false)

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
    ...defaultFormValidation,
  })

  const getOrCreateRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        getFirebaseAuth(),
        recaptchaContainerRef.current!,
        { size: "invisible" },
      )
    }
    return recaptchaVerifierRef.current
  }

  const clearRecaptchaVerifier = () => {
    recaptchaVerifierRef.current?.clear()
    recaptchaVerifierRef.current = null
  }

  // Maps the shared resolver's result onto the form: surface known errors on
  // the form, or navigate to the resolved landing destination. Returns `true`
  // on success so the caller knows a navigation is in flight.
  const handleLoginResult = (result: LoginActionResult): boolean => {
    const resolved = resolveLoginResult(result, {
      redirectTo,
      lastVisitedOrgId: result?.data?.lastVisitedOrgId ?? null,
    })
    if (resolved.kind === "error") {
      form.setError("root.serverError", {
        message: tErrors(resolved.errorKey),
      })
      return false
    }
    trackLogIn()
    setIsNavigating(true)
    router.replace(resolved.path)
    return true
  }

  // Transitions the form into the MFA verification step. Shared between the
  // email/password and OAuth flows because Firebase throws the same
  // `auth/multi-factor-auth-required` error from both `signInWithEmailAndPassword`
  // and `signInWithPopup`.
  const handleMfaChallenge = async (error: unknown): Promise<void> => {
    try {
      const auth = getFirebaseAuth()
      const resolver = getMultiFactorResolver(
        auth,
        error as Parameters<typeof getMultiFactorResolver>[1],
      )
      const phoneHint = resolver.hints.find(
        (h) => h.factorId === PhoneAuthProvider.PROVIDER_ID,
      )
      if (!phoneHint) {
        form.setError("root.serverError", { message: tErrors("generic") })
        return
      }
      const verificationId = await new PhoneAuthProvider(
        auth,
      ).verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        getOrCreateRecaptchaVerifier(),
      )
      setMfaState({
        resolver,
        verificationId,
        phoneHint:
          "phoneNumber" in phoneHint ? (phoneHint.phoneNumber as string) : "",
      })
    } catch (error) {
      console.error("[MFA] verifyPhoneNumber failed:", error)
      clearRecaptchaVerifier()
      form.setError("root.serverError", { message: tErrors("mfaSendFailed") })
    }
  }

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setOauthPending(provider)
    form.clearErrors("root")
    try {
      const firebaseProvider =
        provider === "google"
          ? new GoogleAuthProvider()
          : new FirebaseOAuthProvider("apple.com")
      const credential =
        isPreviewInstance && provider === "google"
          ? await signInGoogleViaProxy()
          : await signInWithPopup(getFirebaseAuth(), firebaseProvider)
      const idToken = await credential.user.getIdToken()
      const result = await loginAction({ idToken })
      handleLoginResult(result)
    } catch (error) {
      const code = (error as FirebaseAuthError)?.code
      if (code === "auth/multi-factor-auth-required") {
        await handleMfaChallenge(error)
        return
      }
      form.setError("root.serverError", {
        message: tErrors(firebaseAuthErrorToMessageKey(code)),
      })
    } finally {
      setOauthPending(null)
    }
  }

  const onSubmit = form.handleSubmit(async ({ email, password }) => {
    form.clearErrors("root")
    try {
      const credential = await signInWithEmailAndPassword(
        getFirebaseAuth(),
        email,
        password,
      )
      const idToken = await credential.user.getIdToken()
      const result = await loginAction({ idToken })
      handleLoginResult(result)
    } catch (error) {
      const code = (error as FirebaseAuthError)?.code
      if (code === "auth/multi-factor-auth-required") {
        await handleMfaChallenge(error)
        return
      }
      form.setError("root.serverError", {
        message: tErrors(firebaseAuthErrorToMessageKey(code)),
      })
    }
  })

  const handleSsoClick = () => {
    trackSso("Continue with SSO on Login Form", {
      email: form.getValues("email"),
    })
  }

  const isSubmitting = form.formState.isSubmitting || isNavigating
  const isAnyPending = isSubmitting || oauthPending !== null
  const rootError = form.formState.errors.root?.serverError

  if (mfaState) {
    return (
      <>
        <div ref={recaptchaContainerRef} />
        <MfaVerifyForm
          {...mfaState}
          redirectTo={redirectTo}
          onBack={() => {
            clearRecaptchaVerifier()
            setMfaState(null)
          }}
        />
      </>
    )
  }

  // Forward the intended destination through the SSO round-trip so the user
  // lands back where they came from after the IdP completes.
  const ssoHref =
    redirectTo !== "/"
      ? `/login/sso?redirectAfterSiroLogin=${encodeURIComponent(redirectTo)}`
      : "/login/sso"

  return (
    <>
      <div ref={recaptchaContainerRef} />
      <form onSubmit={onSubmit} noValidate>
        <FieldGroup>
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid || undefined}>
                <FieldLabel htmlFor="login-email">
                  {t("email.label")}
                </FieldLabel>
                <Input
                  {...field}
                  id="login-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  enterKeyHint="next"
                  placeholder={t("email.placeholder")}
                  aria-invalid={fieldState.invalid || undefined}
                  disabled={isAnyPending}
                />
                {fieldState.invalid && fieldState.error?.message && (
                  <FieldError
                    errors={[{ message: fieldState.error.message }]}
                  />
                )}
              </Field>
            )}
          />
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid || undefined}>
                <FieldLabel htmlFor="login-password">
                  {t("password.label")}
                </FieldLabel>
                <Input
                  {...field}
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  enterKeyHint="go"
                  placeholder={t("password.placeholder")}
                  aria-invalid={fieldState.invalid || undefined}
                  disabled={isAnyPending}
                />
                {fieldState.invalid && fieldState.error?.message && (
                  <FieldError
                    errors={[{ message: fieldState.error.message }]}
                  />
                )}
              </Field>
            )}
          />
          <div className="-mt-2 flex justify-end">
            <Link
              href="/login/reset"
              className="text-xs text-muted-foreground underline-offset-4 hover:underline"
            >
              {t("forgotPassword")}
            </Link>
          </div>
          {rootError?.message && (
            <FieldError errors={[{ message: rootError.message }]} />
          )}
          <Button type="submit" disabled={isAnyPending} className="w-full">
            {isSubmitting ? t("submitting") : t("submit")}
          </Button>
          {/* Two flanking Separators carry the divider semantics; the
              wrapper is layout-only, so no role is needed on it. */}
          <div className="flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">
              {t("oauthDivider")}
            </span>
            <Separator className="flex-1" />
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isAnyPending}
            onClick={() => handleOAuthSignIn("google")}
          >
            <GoogleIcon className="size-4 shrink-0" />
            {oauthPending === "google" ? t("submitting") : t("googleContinue")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isAnyPending}
            onClick={() => handleOAuthSignIn("apple")}
          >
            <AppleIcon className="size-4 shrink-0" />
            {oauthPending === "apple" ? t("submitting") : t("appleContinue")}
          </Button>
          <Button asChild variant="ghost" className="w-full">
            <Link href={ssoHref} onClick={handleSsoClick}>
              {t("ssoContinue")}
            </Link>
          </Button>
          <FieldDescription className="sr-only">
            {t("heading")}
          </FieldDescription>
        </FieldGroup>
      </form>
    </>
  )
}
